import React, { useState, useCallback, useMemo } from 'react';
import { translate } from '@fiverr-private/i18n-react';
import { TabGroup, TabList, Tab } from '@fiverr-private/navigation';
import { Container } from '@fiverr-private/layout_components';
import { ElementType } from '@fiverr-private/bianka_js';
import { BiEventData, onImpression } from '@fiverr-private/store_builder_bi_events';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { ShopifyBuilderIcon } from '../Icons/ShopifyBuilderIcon';
import { FreelancerShopifyIcon } from '../Icons/FreelancerShopifyIcon';
import { NicheCardProps } from '../niche-card/NicheCard.types';
import { reportClickEvent } from '../../utils/report-bi-event/reportClickEvent';
import { TabContent } from './TabContent';
import { type TabItem } from './TabContent.types';

interface StoreBuilderToggleProps {
    showStoreBuilderFlow?: boolean;
    setStoreBuilderFlow: (show: boolean) => void;
    items: NicheCardProps[];
    biEventData: BiEventData;
    pageName: string;
}

export const StoreBuilderToggle: React.FC<StoreBuilderToggleProps> = ({
    showStoreBuilderFlow = false,
    setStoreBuilderFlow,
    biEventData,
    pageName,
}) => {
    const [selectedTab, setSelectedTab] = useState<number>(showStoreBuilderFlow ? 1 : 0);
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: 'viewed_bys_fork_filter',
            componentName: 'bys_fork_filter',
            pageName,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    const tabs: TabItem[] = useMemo(
        () => [
            {
                icon: <FreelancerShopifyIcon />,
                title: translate('store_builder_ui.toggle.shopify_freelancers.title'),
            },
            {
                icon: <ShopifyBuilderIcon />,
                title: translate('store_builder_ui.toggle.free_ai_store_builder.title'),
            },
        ],
        []
    );

    const handleTabChange = useCallback(
        (index: number) => {
            reportClickEvent({
                biEventData,
                eventType: 'clicked_on_shopify_services_or_bys_toggle',
                elementName: 'shopify_services_or_bys_toggle',
                elementType: ElementType.BUTTON,
                pageName,
                storeBuilderToggleChoice: index === 1 ? 'freelancer_store_builder' : 'shopify_services',
            });
            setSelectedTab(index);
            setStoreBuilderFlow(index === 1);
        },
        [setStoreBuilderFlow, biEventData, pageName]
    );

    return (
        <Container ref={pageImpressionRef} display="flex" justifyContent="center" width="100%">
            <TabGroup
                variant="segmentedControlRounded"
                isStretched
                height="100%"
                width={{
                    default: 370,
                    sm: 430,
                }}
                onChange={handleTabChange}
                selectedIndex={selectedTab}
            >
                <TabList justifyContent="stretch" gap={{ default: '1', md: '4', sm: '2' }}>
                    {tabs.map((tab, index) => (
                        <Tab key={index}>
                            <TabContent {...tab} />
                        </Tab>
                    ))}
                </TabList>
            </TabGroup>
        </Container>
    );
};

StoreBuilderToggle.displayName = 'StoreBuilderToggle';
